import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Plus, MinusCircle, RefreshCw, 
  CheckCircle, XCircle, Shield, AlertTriangle,
  Loader, Eye, Save, Calendar
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

const formatInputDate = (date) => date.toISOString().split('T')[0];

const getTrailingDateRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return {
    startDate: formatInputDate(start),
    endDate: formatInputDate(end)
  };
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  return new Date(`${dateString}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

function SearchTermAnalyzer({ selectedAccount }) {
  const { theme, themePreference } = useTheme();
  const { showToast } = useToast();
  const defaultDateRange = getTrailingDateRange(30);
  
  const [activeTab, setActiveTab] = useState('general');
  const [searchTerms, setSearchTerms] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [filterMode, setFilterMode] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedTerms, setSelectedTerms] = useState(new Set());
  const [sortBy, setSortBy] = useState('cost');
  const [sortDir, setSortDir] = useState('desc');
  const [cachedAt, setCachedAt] = useState(null);
  const [cacheMessage, setCacheMessage] = useState(null);
  const [datePreset, setDatePreset] = useState('30');
  const [startDate, setStartDate] = useState(defaultDateRange.startDate);
  const [endDate, setEndDate] = useState(defaultDateRange.endDate);
  const [comparePrevious, setComparePrevious] = useState(false);
  const [period, setPeriod] = useState(null);

  // Brand protection state
  const [brandSettings, setBrandSettings] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [brandVariants, setBrandVariants] = useState('');
  const [brandCampaignIds, setBrandCampaignIds] = useState([]);
  const [brandEnabled, setBrandEnabled] = useState(false);
  const [brandSaving, setBrandSaving] = useState(false);
  const [brandLog, setBrandLog] = useState([]);
  const [loadingBrand, setLoadingBrand] = useState(false);
  const [brandSearchTerms, setBrandSearchTerms] = useState([]);
  const [loadingBrandTerms, setLoadingBrandTerms] = useState(false);
  const [brandApplying, setBrandApplying] = useState({});
  const [campaignFilter, setCampaignFilter] = useState('');
  const [brandDateRange, setBrandDateRange] = useState('LAST_7_DAYS');
  const [brandResultTab, setBrandResultTab] = useState('all');

  const fetchSearchTerms = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const response = await apiCall('/api/search-terms/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 60000,
        body: JSON.stringify({ 
          campaignId: selectedCampaign !== 'all' ? selectedCampaign : undefined,
          startDate,
          endDate,
          comparePrevious
        })
      });
      const data = await response.json();
      if (data.success) {
        setSearchTerms(data.searchTerms || []);
        setCampaigns(data.campaigns || []);
        setCachedAt(data.cachedAt || null);
        setCacheMessage(data.message || null);
        setPeriod(data.period || null);
      }
    } catch (err) {
      showToast('Failed to load search terms: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, selectedCampaign, startDate, endDate, comparePrevious, showToast]);

  const fetchBrandSettings = useCallback(async () => {
    if (!selectedAccount) return;
    setLoadingBrand(true);
    try {
      const response = await apiCall('/api/search-terms/brand-settings');
      const data = await response.json();
      if (data.success && data.settings) {
        setBrandSettings(data.settings);
        setBrandName(data.settings.brand_name || '');
        setBrandVariants((data.settings.brand_variants || []).join(', '));
        const ids = data.settings.campaign_ids || (data.settings.campaign_id ? [data.settings.campaign_id] : []);
        setBrandCampaignIds(ids);
        setBrandEnabled(data.settings.enabled || false);
      }
      if (data.log) {
        setBrandLog(data.log);
      }
    } catch (err) {
      // No settings yet, that's fine
    } finally {
      setLoadingBrand(false);
    }
  }, [selectedAccount]);

  const fetchBrandSearchTerms = useCallback(async () => {
    if (!selectedAccount || brandCampaignIds.length === 0) return;
    setLoadingBrandTerms(true);
    try {
      const response = await apiCall('/api/search-terms/brand-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 60000,
        body: JSON.stringify({
          campaignIds: brandCampaignIds,
          brandName,
          brandVariants: brandVariants.split(',').map(v => v.trim()).filter(Boolean),
          dateRange: brandDateRange
        })
      });
      const data = await response.json();
      if (data.success) {
        setBrandSearchTerms(data.searchTerms || []);
      }
    } catch (err) {
      console.error('Failed to load brand search terms:', err.message);
    } finally {
      setLoadingBrandTerms(false);
    }
  }, [selectedAccount, brandCampaignIds, brandName, brandVariants, brandDateRange]);

  const handleBrandNegate = async (term) => {
    setBrandApplying(prev => ({ ...prev, [term.searchTerm]: 'negating' }));
    const safetyTimeout = setTimeout(() => {
      setBrandApplying(prev => {
        if (prev[term.searchTerm] === 'negating') {
          showToast('Request took too long - keyword may still have been excluded', 'warning');
          return { ...prev, [term.searchTerm]: null };
        }
        return prev;
      });
    }, 30000);
    try {
      const recommendation = {
        id: `negate_st_${term.campaignId}_${term.searchTerm.replace(/\s+/g, '_').substring(0, 30)}`,
        type: 'search_term_optimization',
        subType: 'negate_search_term',
        title: `Negate "${term.searchTerm}" in ${term.campaignName}`,
        campaignId: term.campaignId,
        action: {
          type: 'add_negative_keyword',
          level: 'campaign',
          campaignIds: [term.campaignId],
          keyword: term.searchTerm,
          matchType: 'EXACT'
        }
      };
      const response = await apiCall('/api/optimization/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
        body: JSON.stringify({ recommendation })
      });
      clearTimeout(safetyTimeout);
      const data = await response.json();
      if (response.ok && data.success) {
        setBrandApplying(prev => ({ ...prev, [term.searchTerm]: 'negated' }));
        showToast(`Excluded "${term.searchTerm}"`, 'success');
      } else {
        throw new Error(data.error || `Failed (${response.status})`);
      }
    } catch (err) {
      clearTimeout(safetyTimeout);
      showToast(`Failed: ${err.message}`, 'error');
      setBrandApplying(prev => ({ ...prev, [term.searchTerm]: null }));
    }
  };

  const fetchCampaigns = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const response = await apiCall('/api/search-terms/campaigns');
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error('[SearchTermAnalyzer] Failed to fetch campaigns:', err.message);
    }
  }, [selectedAccount]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    if (activeTab === 'general') {
      fetchSearchTerms();
    } else {
      fetchBrandSettings();
    }
  }, [activeTab, fetchSearchTerms, fetchBrandSettings]);

  const handleAddKeyword = async (term) => {
    setApplying(prev => ({ ...prev, [term.searchTerm]: 'adding' }));
    try {
      const response = await apiCall('/api/search-terms/add-keyword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: term.searchTerm,
          campaignId: term.campaignId,
          adGroupId: term.adGroupId,
          matchType: term.suggestedMatchType || 'EXACT'
        })
      });
      const data = await response.json();
      if (data.success) {
        showToast(`Added "${term.searchTerm}" as keyword`, 'success');
        setApplying(prev => ({ ...prev, [term.searchTerm]: 'added' }));
      } else {
        throw new Error(data.error || 'Failed');
      }
    } catch (err) {
      showToast(`Failed to add keyword: ${err.message}`, 'error');
      setApplying(prev => ({ ...prev, [term.searchTerm]: null }));
    }
  };

  const handleNegateKeyword = async (term) => {
    if (term.campaignType === 'PERFORMANCE_MAX') {
      showToast('PMax campaigns don\'t support campaign-level negatives', 'error');
      return;
    }
    setApplying(prev => ({ ...prev, [term.searchTerm]: 'negating' }));
    const safetyTimeout = setTimeout(() => {
      setApplying(prev => {
        if (prev[term.searchTerm] === 'negating') {
          showToast('Request took too long - keyword may still have been excluded', 'warning');
          return { ...prev, [term.searchTerm]: null };
        }
        return prev;
      });
    }, 30000);
    try {
      const recommendation = {
        id: `st_negate_${term.campaignId}_${term.searchTerm.replace(/\s+/g, '_').substring(0, 30)}`,
        type: 'search_term_optimization',
        subType: 'negate_search_term',
        title: `Negate "${term.searchTerm}" in ${term.campaignName}`,
        campaignId: term.campaignId,
        action: {
          type: 'add_negative_keyword',
          level: 'campaign',
          campaignIds: [term.campaignId],
          keyword: term.searchTerm,
          matchType: 'PHRASE'
        }
      };
      const response = await apiCall('/api/optimization/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
        body: JSON.stringify({ recommendation })
      });
      clearTimeout(safetyTimeout);
      const data = await response.json();
      if (response.ok && data.success) {
        showToast(`Excluded "${term.searchTerm}"`, 'success');
        setApplying(prev => ({ ...prev, [term.searchTerm]: 'negated' }));
      } else {
        throw new Error(data.error || 'Failed');
      }
    } catch (err) {
      clearTimeout(safetyTimeout);
      showToast(`Failed to exclude: ${err.message}`, 'error');
      setApplying(prev => ({ ...prev, [term.searchTerm]: null }));
    }
  };

  const handleBulkAction = async (action) => {
    let terms = searchTerms.filter(t => selectedTerms.has(t.searchTerm));
    if (terms.length === 0) return;

    if (action === 'negate') {
      const pmaxTerms = terms.filter(t => t.campaignType === 'PERFORMANCE_MAX');
      terms = terms.filter(t => t.campaignType !== 'PERFORMANCE_MAX');
      if (pmaxTerms.length > 0) {
        showToast(`Skipped ${pmaxTerms.length} PMax terms (no campaign negatives)`, 'warning');
      }
    }

    for (const term of terms) {
      if (action === 'negate') {
        await handleNegateKeyword(term);
      } else if (action === 'add') {
        await handleAddKeyword(term);
      }
    }
    setSelectedTerms(new Set());
  };

  const handleSaveBrandSettings = async () => {
    if (!brandName.trim()) {
      showToast('Please enter your brand name', 'error');
      return;
    }
    if (brandCampaignIds.length === 0) {
      showToast('Please select at least one brand campaign', 'error');
      return;
    }
    setBrandSaving(true);
    try {
      const variants = brandVariants.split(',').map(v => v.trim()).filter(Boolean);
      const response = await apiCall('/api/search-terms/brand-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandName: brandName.trim(),
          brandVariants: variants,
          campaignIds: brandCampaignIds,
          enabled: brandEnabled
        })
      });
      const data = await response.json();
      if (data.success) {
        showToast('Brand protection settings saved', 'success');
        setBrandSettings(data.settings);
      } else {
        throw new Error(data.error || 'Failed to save');
      }
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setBrandSaving(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedTerms.size === filteredTerms.length) {
      setSelectedTerms(new Set());
    } else {
      setSelectedTerms(new Set(filteredTerms.map(t => t.searchTerm)));
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const handlePresetChange = (value) => {
    setDatePreset(value);
    if (value === 'custom') return;
    const nextRange = getTrailingDateRange(Number(value));
    setStartDate(nextRange.startDate);
    setEndDate(nextRange.endDate);
  };

  const handleDateChange = (field, value) => {
    setDatePreset('custom');
    if (field === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  const getTrendLabel = (currentValue, previousValue, prefix = '') => {
    if (!comparePrevious || previousValue === null || previousValue === undefined) return null;
    const diff = currentValue - previousValue;
    if (Math.abs(diff) < 0.01) return 'No change';
    const sign = diff > 0 ? '+' : '';
    return `${sign}${prefix}${diff.toFixed(prefix ? 2 : 0)} vs previous`;
  };

  const filteredTerms = searchTerms
    .filter(t => {
      if (filterMode === 'irrelevant') return t.isIrrelevant;
      if (filterMode === 'converting') return t.conversions > 0;
      if (filterMode === 'zero-conv') return t.conversions === 0 && (t.negativeRuleMatched || t.clicks >= (t.negativeRuleThreshold || 10));
      return true;
    })
    .filter(t => !searchFilter || t.searchTerm.toLowerCase().includes(searchFilter.toLowerCase()))
    .sort((a, b) => {
      const mul = sortDir === 'asc' ? 1 : -1;
      return (a[sortBy] - b[sortBy]) * mul;
    });

  const styles = {
    container: {
      padding: '32px',
      maxWidth: 1400,
      margin: '0 auto'
    },
    header: {
      marginBottom: 24
    },
    title: {
      fontSize: 26,
      fontWeight: 700,
      color: theme.colors.text.primary,
      margin: 0
    },
    subtitle: {
      fontSize: 14,
      color: theme.colors.text.secondary,
      margin: '4px 0 0'
    },
    tabs: {
      display: 'flex',
      gap: 0,
      marginBottom: 24,
      borderBottom: `1px solid ${theme.colors.border.primary}`
    },
    tab: (active) => ({
      padding: '12px 24px',
      fontSize: 14,
      fontWeight: 600,
      color: active ? '#6366f1' : theme.colors.text.secondary,
      borderBottom: active ? '2px solid #6366f1' : '2px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: 'none',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }),
    controls: {
      display: 'flex',
      gap: 12,
      marginBottom: 16,
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    datePanel: {
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      flexWrap: 'wrap',
      padding: 16,
      marginBottom: 16,
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12
    },
    dateInput: {
      padding: '8px 12px',
      borderRadius: 8,
      border: `1px solid ${theme.colors.border.primary}`,
      background: theme.colors.bg.primary,
      color: theme.colors.text.primary,
      fontSize: 13,
      outline: 'none',
      colorScheme: themePreference === 'light' ? 'light' : 'dark'
    },
    select: {
      padding: '8px 12px',
      borderRadius: 8,
      border: `1px solid ${theme.colors.border.primary}`,
      background: theme.colors.bg.secondary,
      color: theme.colors.text.primary,
      fontSize: 13,
      outline: 'none'
    },
    searchInput: {
      padding: '8px 12px',
      borderRadius: 8,
      border: `1px solid ${theme.colors.border.primary}`,
      background: theme.colors.bg.secondary,
      color: theme.colors.text.primary,
      fontSize: 13,
      outline: 'none',
      width: 220
    },
    button: (variant = 'default') => ({
      padding: '8px 16px',
      borderRadius: 8,
      border: variant === 'default' ? `1px solid ${theme.colors.border.primary}` : 'none',
      background: variant === 'primary' ? '#6366f1' : variant === 'danger' ? '#ef4444' : variant === 'success' ? '#10b981' : theme.colors.bg.secondary,
      color: variant === 'default' ? theme.colors.text.primary : '#fff',
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      transition: 'all 0.2s'
    }),
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12,
      overflow: 'hidden'
    },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: 12,
      fontWeight: 600,
      color: theme.colors.text.secondary,
      background: theme.colors.bg.tertiary || theme.colors.bg.secondary,
      borderBottom: `1px solid ${theme.colors.border.primary}`,
      cursor: 'pointer',
      userSelect: 'none'
    },
    td: {
      padding: '12px 16px',
      fontSize: 13,
      color: theme.colors.text.primary,
      borderBottom: `1px solid ${theme.colors.border.primary}`
    },
    card: {
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12,
      padding: 24,
      marginBottom: 16
    },
    badge: (color) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '3px 8px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: `${color}15`,
      color: color
    }),
    label: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.colors.text.primary,
      marginBottom: 6,
      display: 'block'
    },
    input: {
      width: '100%',
      padding: '10px 14px',
      borderRadius: 8,
      border: `1px solid ${theme.colors.border.primary}`,
      background: theme.colors.bg.primary,
      color: theme.colors.text.primary,
      fontSize: 14,
      outline: 'none',
      marginBottom: 16
    }
  };

  const renderGeneralTab = () => (
    <>
      <div style={styles.datePanel}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.colors.text.primary, fontSize: 13, fontWeight: 600 }}>
          <Calendar size={16} />
          Date range
        </div>
        <select
          style={styles.select}
          value={datePreset}
          onChange={e => handlePresetChange(e.target.value)}
        >
          <option value="7">Last 7 days</option>
          <option value="14">Last 14 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="custom">Custom range</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.colors.text.secondary }}>
          From
          <input
            type="date"
            style={styles.dateInput}
            value={startDate}
            max={endDate}
            onChange={e => handleDateChange('start', e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.colors.text.secondary }}>
          To
          <input
            type="date"
            style={styles.dateInput}
            value={endDate}
            min={startDate}
            onChange={e => handleDateChange('end', e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: theme.colors.text.primary }}>
          <input
            type="checkbox"
            checked={comparePrevious}
            onChange={e => setComparePrevious(e.target.checked)}
            style={{ width: 16, height: 16 }}
          />
          Compare with previous period
        </label>
        <button style={styles.button('primary')} onClick={fetchSearchTerms} disabled={loading || !startDate || !endDate}>
          <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          Apply range
        </button>
      </div>

      <div style={styles.controls}>
        <select 
          style={styles.select} 
          value={selectedCampaign} 
          onChange={e => setSelectedCampaign(e.target.value)}
        >
          <option value="all">All Campaigns</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select 
          style={styles.select} 
          value={filterMode} 
          onChange={e => setFilterMode(e.target.value)}
        >
          <option value="all">All Search Terms</option>
          <option value="irrelevant">Likely Irrelevant</option>
          <option value="converting">Converting</option>
          <option value="zero-conv">Zero Conversions (rule match)</option>
        </select>

        <input
          type="text"
          placeholder="Filter search terms..."
          style={styles.searchInput}
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
        />

        <button style={styles.button()} onClick={fetchSearchTerms} disabled={loading}>
          <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          Refresh
        </button>

        {selectedTerms.size > 0 && (
          <>
            <button style={styles.button('success')} onClick={() => handleBulkAction('add')}>
              <Plus size={14} /> Add Selected ({selectedTerms.size})
            </button>
            <button style={styles.button('danger')} onClick={() => handleBulkAction('negate')}>
              <MinusCircle size={14} /> Exclude Selected ({selectedTerms.size})
            </button>
          </>
        )}
      </div>

      {cachedAt && (
        <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginBottom: 12 }}>
          Last updated: {new Date(cachedAt).toLocaleString()}
        </div>
      )}

      {period && (
        <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <span>
            Showing: {formatDisplayDate(period.startDate)} - {formatDisplayDate(period.endDate)}
          </span>
          {period.comparePrevious && period.previousStartDate && (
            <span>
              Compared with: {formatDisplayDate(period.previousStartDate)} - {formatDisplayDate(period.previousEndDate)}
            </span>
          )}
        </div>
      )}

      {cacheMessage && filteredTerms.length === 0 && !loading && (
        <div style={{ ...styles.card, display: 'flex', alignItems: 'center', gap: 12 }}>
          <AlertTriangle size={18} color="#f59e0b" />
          <span style={{ fontSize: 13, color: theme.colors.text.secondary }}>{cacheMessage}</span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: theme.colors.text.secondary }}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
          <p>Loading search terms...</p>
        </div>
      ) : filteredTerms.length === 0 && !cacheMessage ? (
        <div style={{ textAlign: 'center', padding: 60, color: theme.colors.text.secondary }}>
          <Search size={32} style={{ marginBottom: 12, opacity: 0.5 }} />
          <p>No search terms found. Select a campaign and try again.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 40 }}>
                  <input 
                    type="checkbox" 
                    checked={selectedTerms.size === filteredTerms.length && filteredTerms.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={styles.th} onClick={() => handleSort('searchTerm')}>
                  Search Term {sortBy === 'searchTerm' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th style={styles.th}>Campaign</th>
                <th style={styles.th} onClick={() => handleSort('clicks')}>
                  Clicks {comparePrevious ? '/ Prev.' : ''} {sortBy === 'clicks' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th style={styles.th} onClick={() => handleSort('impressions')}>
                  Impr. {sortBy === 'impressions' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th style={styles.th} onClick={() => handleSort('cost')}>
                  Cost {comparePrevious ? '/ Prev.' : ''} {sortBy === 'cost' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th style={styles.th} onClick={() => handleSort('conversions')}>
                  Conv. {comparePrevious ? '/ Prev.' : ''} {sortBy === 'conversions' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th style={styles.th} onClick={() => handleSort('ctr')}>
                  CTR {sortBy === 'ctr' && (sortDir === 'asc' ? '↑' : '↓')}
                </th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTerms.slice(0, 200).map((term, i) => {
                const status = applying[term.searchTerm];
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : theme.colors.bg.tertiary || 'transparent' }}>
                    <td style={styles.td}>
                      <input 
                        type="checkbox"
                        checked={selectedTerms.has(term.searchTerm)}
                        onChange={() => {
                          const next = new Set(selectedTerms);
                          if (next.has(term.searchTerm)) next.delete(term.searchTerm);
                          else next.add(term.searchTerm);
                          setSelectedTerms(next);
                        }}
                      />
                    </td>
                    <td style={{ ...styles.td, fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {term.searchTerm}
                      {term.isIrrelevant && (
                        <span style={styles.badge('#ef4444')}>
                          <AlertTriangle size={10} /> Irrelevant
                        </span>
                      )}
                      {term.negativeRuleMatched && (
                        <span style={styles.badge('#f59e0b')}>
                          <MinusCircle size={10} /> {term.negativeRuleThresholdSource === 'custom' ? `${term.negativeRuleThreshold}+` : `>${term.negativeRuleThreshold || 10}`} clicks / 0 conv
                        </span>
                      )}
                      {term.relevanceSource === 'business_profile_match' && !term.isIrrelevant && (
                        <span style={styles.badge('#10b981')}>
                          <CheckCircle size={10} /> Relevant
                        </span>
                      )}
                    </td>
                    <td style={{ ...styles.td, fontSize: 12, color: theme.colors.text.secondary, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {term.campaignName}
                      {term.campaignType === 'PERFORMANCE_MAX' && (
                        <span style={{ ...styles.badge('#8b5cf6'), marginLeft: 4 }}>PMax</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div>{term.clicks}</div>
                      {term.previous && (
                        <div style={{ fontSize: 11, color: theme.colors.text.secondary }}>
                          prev. {term.previous.clicks} · {getTrendLabel(term.clicks, term.previous.clicks)}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{term.impressions}</td>
                    <td style={styles.td}>
                      <div>€{term.cost.toFixed(2)}</div>
                      {term.previous && (
                        <div style={{ fontSize: 11, color: theme.colors.text.secondary }}>
                          prev. €{term.previous.cost.toFixed(2)} · {getTrendLabel(term.cost, term.previous.cost, '€')}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={{ color: term.conversions > 0 ? '#10b981' : theme.colors.text.secondary }}>
                        {term.conversions.toFixed(1)}
                      </span>
                      {term.previous && (
                        <div style={{ fontSize: 11, color: theme.colors.text.secondary }}>
                          prev. {term.previous.conversions.toFixed(1)}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{(term.ctr * 100).toFixed(1)}%</td>
                    <td style={styles.td}>
                      {term.existingStatus === 'ADDED' ? (
                        <span style={styles.badge('#10b981')}><CheckCircle size={10} /> Added</span>
                      ) : term.existingStatus === 'EXCLUDED' ? (
                        <span style={styles.badge('#ef4444')}><XCircle size={10} /> Excluded</span>
                      ) : (
                        <span style={styles.badge('#64748b')}>None</span>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      {status === 'added' ? (
                        <span style={{ color: '#10b981', fontSize: 12 }}>✓ Added</span>
                      ) : status === 'negated' ? (
                        <span style={{ color: '#ef4444', fontSize: 12 }}>✓ Excluded</span>
                      ) : status ? (
                        <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            style={{ ...styles.button('success'), padding: '5px 10px', fontSize: 11 }}
                            onClick={() => handleAddKeyword(term)}
                            title="Add as keyword"
                          >
                            <Plus size={12} /> Add
                          </button>
                          {term.campaignType === 'PERFORMANCE_MAX' ? (
                            <span style={{ fontSize: 10, color: theme.colors.text.secondary, padding: '5px 6px' }}>
                              PMax (no negatives)
                            </span>
                          ) : (
                            <button
                              style={{ ...styles.button('danger'), padding: '5px 10px', fontSize: 11 }}
                              onClick={() => handleNegateKeyword(term)}
                              title="Exclude as negative"
                            >
                              <MinusCircle size={12} /> Exclude
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredTerms.length > 200 && (
            <p style={{ textAlign: 'center', padding: 12, color: theme.colors.text.secondary, fontSize: 13 }}>
              Showing 200 of {filteredTerms.length} results. Use filters to narrow down.
            </p>
          )}
        </div>
      )}
    </>
  );

  const renderBrandTab = () => (
    <div>
      <div style={{ ...styles.card, maxWidth: 700 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#6366f115', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={20} color="#6366f1" />
          </div>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Brand Campaign Protector</h3>
            <p style={{ fontSize: 13, color: theme.colors.text.secondary, margin: 0 }}>
              Automatically exclude non-brand search terms from your brand campaigns every night.
            </p>
          </div>
        </div>

        {loadingBrand ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <>
            <label style={styles.label}>Brand Name</label>
            <input
              type="text"
              placeholder="e.g. Bidoryx"
              style={styles.input}
              value={brandName}
              onChange={e => setBrandName(e.target.value)}
            />

            <label style={styles.label}>
              Alternative Spellings / Variants
              <span style={{ fontWeight: 400, color: theme.colors.text.secondary }}> (comma-separated)</span>
            </label>
            <input
              type="text"
              placeholder="e.g. bid oryx, bidorix, bidoriks"
              style={styles.input}
              value={brandVariants}
              onChange={e => setBrandVariants(e.target.value)}
            />

            <label style={styles.label}>Brand Campaigns (select multiple)</label>
            <input
              type="text"
              placeholder="Search campaigns..."
              style={{ ...styles.input, marginBottom: 0, borderRadius: '8px 8px 0 0' }}
              value={campaignFilter}
              onChange={e => setCampaignFilter(e.target.value)}
            />
            <div style={{ ...styles.input, padding: 0, marginBottom: 16, maxHeight: 200, overflowY: 'auto', borderRadius: '0 0 8px 8px', borderTop: 'none' }}>
              {campaigns.length === 0 ? (
                <p style={{ padding: 12, fontSize: 13, color: theme.colors.text.secondary, margin: 0 }}>Loading campaigns...</p>
              ) : campaigns
                .filter(c => c.type !== 'PERFORMANCE_MAX')
                .filter(c => !campaignFilter || c.name.toLowerCase().includes(campaignFilter.toLowerCase()))
                .map(c => (
                <label key={c.id} style={{ 
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', 
                  cursor: 'pointer', borderBottom: `1px solid ${theme.colors.border.primary}`,
                  background: brandCampaignIds.includes(c.id) ? '#6366f108' : 'transparent'
                }}>
                  <input
                    type="checkbox"
                    checked={brandCampaignIds.includes(c.id)}
                    onChange={() => {
                      setBrandCampaignIds(prev => 
                        prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id]
                      );
                    }}
                  />
                  <span style={{ fontSize: 13, color: theme.colors.text.primary }}>{c.name}</span>
                  {c.type && <span style={{ fontSize: 11, color: theme.colors.text.secondary }}>({c.type})</span>}
                </label>
              ))}
            </div>
            {brandCampaignIds.length > 0 && (
              <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: -12, marginBottom: 16 }}>
                {brandCampaignIds.length} campaign{brandCampaignIds.length > 1 ? 's' : ''} selected
              </p>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: theme.colors.text.primary }}>
                <input
                  type="checkbox"
                  checked={brandEnabled}
                  onChange={e => setBrandEnabled(e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                Enable automatic nightly exclusion
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                style={{ ...styles.button('primary'), padding: '12px 24px' }}
                onClick={handleSaveBrandSettings}
                disabled={brandSaving}
              >
                {brandSaving ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
                Save Settings
              </button>
              {brandCampaignIds.length > 0 && brandName && (
                <>
                  <select
                    style={styles.select}
                    value={brandDateRange}
                    onChange={e => setBrandDateRange(e.target.value)}
                  >
                    <option value="LAST_7_DAYS">Last 7 days</option>
                    <option value="LAST_30_DAYS">Last 30 days</option>
                    <option value="LAST_90_DAYS">Last 90 days</option>
                  </select>
                  <button
                    style={{ ...styles.button(), padding: '12px 24px' }}
                    onClick={fetchBrandSearchTerms}
                    disabled={loadingBrandTerms}
                  >
                    {loadingBrandTerms ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Eye size={14} />}
                    Show Non-Brand Terms
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {brandSearchTerms.length > 0 && (() => {
        const campaignGroups = {};
        brandSearchTerms.forEach(t => {
          const key = t.campaignId;
          if (!campaignGroups[key]) campaignGroups[key] = { name: t.campaignName, terms: [] };
          campaignGroups[key].terms.push(t);
        });
        const campaignKeys = Object.keys(campaignGroups);
        const visibleTerms = brandResultTab === 'all' 
          ? brandSearchTerms 
          : (campaignGroups[brandResultTab]?.terms || []);

        return (
          <div style={{ ...styles.card, marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
                Non-Brand Search Terms ({brandSearchTerms.length})
              </h4>
              <span style={{ fontSize: 12, color: theme.colors.text.secondary }}>
                Exclude irrelevant terms to protect your brand campaign.
              </span>
            </div>

            {campaignKeys.length > 1 && (
              <div style={{ display: 'flex', gap: 0, marginBottom: 12, overflowX: 'auto', borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                <button
                  style={styles.tab(brandResultTab === 'all')}
                  onClick={() => setBrandResultTab('all')}
                >
                  All ({brandSearchTerms.length})
                </button>
                {campaignKeys.map(key => (
                  <button
                    key={key}
                    style={{ ...styles.tab(brandResultTab === key), whiteSpace: 'nowrap' }}
                    onClick={() => setBrandResultTab(key)}
                  >
                    {campaignGroups[key].name} ({campaignGroups[key].terms.length})
                  </button>
                ))}
              </div>
            )}

            <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Search Term</th>
                    {brandResultTab === 'all' && <th style={styles.th}>Campaign</th>}
                    <th style={styles.th}>Clicks</th>
                    <th style={styles.th}>Cost</th>
                    <th style={styles.th}>Conv.</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTerms.map((term, i) => {
                    const status = brandApplying[term.searchTerm];
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : theme.colors.bg.tertiary || 'transparent' }}>
                        <td style={{ ...styles.td, fontWeight: 500 }}>{term.searchTerm}</td>
                        {brandResultTab === 'all' && <td style={{ ...styles.td, fontSize: 12, color: theme.colors.text.secondary }}>{term.campaignName}</td>}
                        <td style={styles.td}>{term.clicks}</td>
                        <td style={styles.td}>€{term.cost.toFixed(2)}</td>
                        <td style={styles.td}>{term.conversions.toFixed(1)}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          {status === 'negated' ? (
                            <span style={{ color: '#ef4444', fontSize: 12 }}>✓ Excluded</span>
                          ) : status ? (
                            <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <button
                              style={{ ...styles.button('danger'), padding: '5px 10px', fontSize: 11 }}
                              onClick={() => handleBrandNegate(term)}
                            >
                              <MinusCircle size={12} /> Exclude
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {brandEnabled && brandSettings && (
        <div style={{ ...styles.card, marginTop: 16, maxWidth: 700 }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 12 }}>
            Exclusion Log (Last 7 Days)
          </h4>
          {brandLog.length === 0 ? (
            <p style={{ fontSize: 13, color: theme.colors.text.secondary }}>
              No exclusions yet. The brand protector runs nightly and will appear here after the first run.
            </p>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {brandLog.map((entry, i) => (
                <div key={i} style={{ 
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 12px', borderRadius: 6,
                  background: i % 2 === 0 ? 'transparent' : theme.colors.bg.tertiary || 'transparent'
                }}>
                  <span style={{ fontSize: 13, color: theme.colors.text.primary }}>
                    <MinusCircle size={12} color="#ef4444" style={{ marginRight: 6 }} />
                    {entry.term}
                  </span>
                  <span style={{ fontSize: 11, color: theme.colors.text.secondary }}>
                    {new Date(entry.excluded_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <Search size={24} color="#fff" />
          </div>
          <div>
            <h1 style={styles.title}>Search Term Analyzer</h1>
            <p style={styles.subtitle}>
              Analyze search terms, add top performers as keywords, and exclude irrelevant traffic.
            </p>
          </div>
        </div>
      </div>

      <div style={styles.tabs}>
        <button 
          style={styles.tab(activeTab === 'general')} 
          onClick={() => setActiveTab('general')}
        >
          <Search size={16} /> Search Terms
        </button>
        <button 
          style={styles.tab(activeTab === 'brand')} 
          onClick={() => setActiveTab('brand')}
        >
          <Shield size={16} /> Brand Protector
        </button>
      </div>

      {activeTab === 'general' ? renderGeneralTab() : renderBrandTab()}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default SearchTermAnalyzer;
